import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, ErrorState } from "@/components/States";
import { SelectionForm } from "@/components/SelectionForm";
import { usePlan } from "@/lib/plan";
import { UpgradeNote } from "@/components/UpgradeNote";
import { Switch } from "@/components/ui/switch";
import QuizPlayer from "@/components/QuizPlayer";
import QuizResult from "@/components/QuizResult";
import { toast } from "sonner";
import { fetchSubjects, formatError, runJob } from "@/lib/api";

export default function Quiz() {
  const { plan, limits, reload } = usePlan();
  const quotaUsed = limits && plan.usage.quizzes >= limits.quizzes;
  const per = limits?.period === "day" ? "today" : "this month";
  const [params] = useSearchParams();
  const [subjects, setSubjects] = useState(null);
  const [sel, setSel] = useState({ subject_id: params.get("subject") || "", topic: params.get("topic") || "", difficulty: params.get("difficulty") || "beginner", count: 5, source: "ai", timed: false });
  const [session, setSession] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { fetchSubjects().then(setSubjects).catch((e) => setErr(formatError(e))); }, []);

  const start = async () => {
    setBusy(true); setErr(""); setAttempt(null); setSession(null);
    try {
      const s = await runJob("/quiz/start", sel);
      if (s.notice) toast.info(s.notice);
      setSession(s);
    } catch (e) { setErr(formatError(e)); }
    reload();
    setBusy(false);
  };

  if (!subjects) return err ? <ErrorState message={err} /> : <Loading />;
  if (busy) return <Loading label={sel.source === "ai" ? "Generating and validating your questions (about 20–40 seconds)..." : "Picking questions from the approved bank..."} />;
  if (attempt) return <QuizResult attempt={attempt} onRetake={start} retakeLabel={sel.source === "ai" ? "Retake with new questions" : "Retake quiz"} />;
  if (session) return <QuizPlayer key={session.id} session={session} onFinish={setAttempt} />;

  return (
    <div>
      <PageHeader eyebrow="Quiz generator" title="Test your knowledge" text="Multiple-choice questions with validated answer keys, instant feedback and a full review at the end." />
      <div className="rounded-2xl border border-line bg-surface p-6">
        <SelectionForm limits={limits} subjects={subjects} value={sel} onChange={setSel} topicRequired={false} counts={[5, 10, 20]} showSource />
        <div className="mt-5 flex items-center gap-3">
          <Switch id="quiz-timed" data-testid="quiz-timed-toggle" checked={sel.timed} disabled={!!limits && !limits.timed_quiz} onCheckedChange={(v) => setSel({ ...sel, timed: v })} />
          <label htmlFor="quiz-timed" className="text-sm font-medium text-ink">Timed quiz <span className="font-normal text-ink-3">(1 minute per question)</span>
            {limits && !limits.timed_quiz && <span data-testid="quiz-timed-pro" className="ml-1.5 rounded-full bg-aqua-soft px-2 py-0.5 text-[10px] font-semibold text-aqua">Pro</span>}</label>
        </div>
        {quotaUsed && <div className="mt-5"><UpgradeNote compact testId="quiz-quota-used" text={`You've used all ${limits.quizzes} quiz${limits.quizzes === 1 ? "" : "zes"} for ${per} on the ${plan.plan_name} plan. The daily challenge quiz is still open, or upgrade for more.`} /></div>}
        {err && <div className="mt-5"><ErrorState message={err} /></div>}
        <Button data-testid="quiz-start-btn" disabled={!sel.subject_id || quotaUsed} onClick={start} className="mt-6 rounded-full px-6">
          <ListChecks className="mr-2 h-4 w-4" />Start {sel.count}-question quiz
        </Button>
      </div>
    </div>
  );
}
